import { PencilSquareIcon, TrashIcon, UserCircleIcon } from '@heroicons/react/24/outline'
import { useEffect, useState } from 'react'
import ROUTES from '@/ROUTES'

import { api, api_delete, api_patch } from '@/services/api.service'

import { useProviderSession } from './ProviderSession'

interface ProviderNote {
    _id: string
    body: string
    authorName: string | null
    isOwn: boolean
    createdAt: string
    edited: boolean
}

function formatDateTime(value?: string | null) {
    if (!value) return null
    const d = new Date(value)
    if (isNaN(d.valueOf())) return null
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}

// Internal coordinator notes for one family. Org-only record-keeping: nothing
// here is ever shown in the family app, and notes are available regardless of
// the family's detail-sharing consent.
export default function FamilyNotesSection({ licenseId }: { licenseId: string }) {
    const session = useProviderSession()
    // Viewer is read-only: no composer, no delete. Edit is already author-only.
    const canWrite = session?.role !== 'viewer'
    const [notes, setNotes] = useState<ProviderNote[]>([])
    const [loaded, setLoaded] = useState(false)
    const [draft, setDraft] = useState('')
    const [adding, setAdding] = useState(false)
    const [addError, setAddError] = useState<string | null>(null)

    useEffect(() => {
        let cancelled = false
        async function load() {
            const response = await api<{ notes: ProviderNote[] }>(`${ROUTES.provider.note}/${licenseId}`)
            if (cancelled) return
            if (response.status && response.status < 400 && response.data) {
                setNotes(response.data.notes)
            }
            setLoaded(true)
        }
        load()
        return () => {
            cancelled = true
        }
    }, [licenseId])

    async function handleAdd() {
        if (!draft.trim()) return
        setAddError(null)
        setAdding(true)
        // suppressForbiddenRedirect: a role-denied 403 (e.g. viewer) should show
        // inline here, not bounce the user to /forbidden mid-task.
        const response = await api<ProviderNote>(
            `${ROUTES.provider.note}/${licenseId}`,
            { body: draft.trim() },
            undefined,
            undefined,
            true
        )
        setAdding(false)
        if (response.status && response.status < 400 && response.data) {
            setNotes((prev) => [response.data!, ...prev])
            setDraft('')
        } else {
            setAddError(response.error || 'Failed to add note')
        }
    }

    function handleUpdated(updated: ProviderNote) {
        setNotes((prev) => prev.map((n) => (n._id === updated._id ? updated : n)))
    }

    function handleDeleted(noteId: string) {
        setNotes((prev) => prev.filter((n) => n._id !== noteId))
    }

    return (
        <section className='bg-white border border-neutral-200 rounded-2xl p-6 mt-6'>
            <h2 className='text-lg font-semibold text-neutral-900 mb-1'>Coordinator notes</h2>
            <p className='text-sm text-neutral-600 mb-4'>
                Internal to your organization. The family never sees these.
            </p>

            {canWrite && (
                <div className='mb-5'>
                    <textarea
                        rows={3}
                        value={draft}
                        onChange={(e) => setDraft(e.target.value)}
                        maxLength={5000}
                        placeholder='Add a note about this family…'
                        className='w-full px-3 py-2 border border-neutral-300 rounded-lg text-sm'
                    />
                    {addError && <p className='mt-1 text-sm text-red-600'>{addError}</p>}
                    <button
                        onClick={handleAdd}
                        disabled={adding || !draft.trim()}
                        className='mt-2 px-4 py-2 text-sm font-medium text-white bg-brand-600 hover:bg-brand-700 rounded-lg disabled:opacity-50'
                    >
                        {adding ? 'Adding…' : 'Add note'}
                    </button>
                </div>
            )}

            {!loaded ? (
                <div className='text-sm text-neutral-500'>Loading notes…</div>
            ) : notes.length === 0 ? (
                <div className='text-sm text-neutral-500'>No notes yet.</div>
            ) : (
                <ul className='space-y-3'>
                    {notes.map((note) => (
                        <NoteRow
                            key={note._id}
                            note={note}
                            licenseId={licenseId}
                            canWrite={canWrite}
                            onUpdated={handleUpdated}
                            onDeleted={handleDeleted}
                        />
                    ))}
                </ul>
            )}
        </section>
    )
}

function NoteRow({
    note,
    licenseId,
    canWrite,
    onUpdated,
    onDeleted,
}: {
    note: ProviderNote
    licenseId: string
    canWrite: boolean
    onUpdated: (note: ProviderNote) => void
    onDeleted: (noteId: string) => void
}) {
    const [editing, setEditing] = useState(false)
    const [editDraft, setEditDraft] = useState(note.body)
    const [saving, setSaving] = useState(false)
    const [deleteState, setDeleteState] = useState<'idle' | 'confirm' | 'deleting'>('idle')
    const [error, setError] = useState<string | null>(null)

    async function handleSave() {
        if (!editDraft.trim()) return
        setError(null)
        setSaving(true)
        const response = await api_patch<ProviderNote>(`${ROUTES.provider.note}/${licenseId}/${note._id}`, {
            body: editDraft.trim(),
        })
        setSaving(false)
        if (response.status && response.status < 400 && response.data) {
            onUpdated(response.data)
            setEditing(false)
        } else {
            setError(response.error || 'Failed to save note')
        }
    }

    async function handleDelete() {
        setError(null)
        setDeleteState('deleting')
        const response = await api_delete(`${ROUTES.provider.note}/${licenseId}/${note._id}`)
        if (response.status && response.status < 400) {
            onDeleted(note._id)
        } else {
            setDeleteState('idle')
            setError(response.error || 'Failed to delete note')
        }
    }

    return (
        <li className='border border-neutral-200 rounded-xl p-4'>
            <div className='flex items-center justify-between gap-2 mb-2'>
                <div className='flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-neutral-500'>
                    <span className='inline-flex items-center gap-1'>
                        <UserCircleIcon className='w-3.5 h-3.5' />
                        <span className='font-medium text-neutral-700'>{note.authorName ?? 'Unknown'}</span>
                    </span>
                    <span>·</span>
                    <span>{formatDateTime(note.createdAt)}</span>
                    {note.edited && (
                        <>
                            <span>·</span>
                            <span className='italic'>edited</span>
                        </>
                    )}
                </div>
                {!editing && canWrite && (
                    <div className='flex items-center gap-1 flex-shrink-0'>
                        {note.isOwn && (
                            <button
                                onClick={() => {
                                    setEditDraft(note.body)
                                    setEditing(true)
                                    setError(null)
                                }}
                                title='Edit note'
                                className='p-1.5 text-neutral-400 hover:text-brand-700 hover:bg-brand-50 rounded-lg'
                            >
                                <PencilSquareIcon className='w-4 h-4' />
                            </button>
                        )}
                        {deleteState === 'confirm' ? (
                            <span className='flex items-center gap-1.5'>
                                <button
                                    onClick={handleDelete}
                                    className='px-2 py-1 text-xs font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg'
                                >
                                    Confirm delete
                                </button>
                                <button
                                    onClick={() => setDeleteState('idle')}
                                    className='px-2 py-1 text-xs font-medium text-neutral-600 hover:bg-neutral-100 rounded-lg'
                                >
                                    Keep
                                </button>
                            </span>
                        ) : (
                            <button
                                onClick={() => setDeleteState('confirm')}
                                disabled={deleteState === 'deleting'}
                                title='Delete note'
                                className='p-1.5 text-neutral-400 hover:text-red-700 hover:bg-red-50 rounded-lg disabled:opacity-50'
                            >
                                <TrashIcon className='w-4 h-4' />
                            </button>
                        )}
                    </div>
                )}
            </div>

            {editing ? (
                <div>
                    <textarea
                        rows={3}
                        value={editDraft}
                        onChange={(e) => setEditDraft(e.target.value)}
                        maxLength={5000}
                        className='w-full px-3 py-2 border border-neutral-300 rounded-lg text-sm'
                    />
                    <div className='mt-2 flex items-center gap-2'>
                        <button
                            onClick={handleSave}
                            disabled={saving || !editDraft.trim()}
                            className='px-3 py-1.5 text-xs font-medium text-white bg-brand-600 hover:bg-brand-700 rounded-lg disabled:opacity-50'
                        >
                            {saving ? 'Saving…' : 'Save'}
                        </button>
                        <button
                            onClick={() => {
                                setEditing(false)
                                setError(null)
                            }}
                            className='px-3 py-1.5 text-xs font-medium text-neutral-600 hover:bg-neutral-100 rounded-lg'
                        >
                            Cancel
                        </button>
                    </div>
                </div>
            ) : (
                <p className='text-sm text-neutral-800 whitespace-pre-wrap'>{note.body}</p>
            )}

            {error && <p className='mt-2 text-xs text-red-600'>{error}</p>}
        </li>
    )
}
